/* eslint-disable max-lines */
import {
    AnalyticsContext,
    getBridgeSdk,
    CHECKOUT_WEB_XP,
    GroupOrderCartContext,
    CheckoutWebDynamicConfigContext,
    isPopFlow,
    NEWRELIC_TABLES,
    recordNREvent,
} from "@swiggy-private/checkout-core";
import { BOOLEAN_AS_STRING, SliderButtonRef } from "@swiggy-private/payment-types";
import { VibrateAmplitudeTypes } from "@swiggy-private/native-bridge";
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef } from "react";
//internal-deps
import { SliderButtonContext, TurboPaymentWidget, TurboCartFallbackFooter } from "@swiggy-private/payment-ui";
import {
    GenericPaymentAlertContext,
    DEFAULT_PAYMENT_ALERT_PROPS,
} from "@payment-client/contexts/GenericPaymentAlertContext";
import { isEmpty, TurboStripCtaLink } from "@swiggy-private/core-ui";
//analytics
import {
    onClickChange,
    onClickChangeTooltip,
    onClickSlider,
    onImpressionChangeTooltip,
} from "@payment-client/analytics/events/TurboPayment";

import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
//components
import { ShowPayments } from "@payment-client/components/show-payments";
// contexts
import { UpiIntentContext } from "@payment-client/contexts/UpiIntentContext";
import { impressionPayAfterOrder } from "@payment-client/analytics/events/PayOnDelivery";
import { manageSwipe } from "@swiggy-private/checkout-core";
import { onUpiAppImpression } from "@payment-client/analytics/events/Payment";
//hooks
import { useTurboPaymentHandler } from "@payment-client/hooks/useTurboPaymentHandler";
import { useBillToCompanyDetails } from "@payment-client/hooks/useBillToCompanyDetails";
import CartFooterPaymentWidgets from "./CartFooterPaymentWidgets";
//constants
import { PAYMENT_INITIAL_STATE } from "@payment-client/components/payment-main/PaymentMain";
// types
import { TurboPaymentProps } from "./types";

const TurboPaymentWrapper = (props: TurboPaymentProps) => {
    const { isTurboV2 = false } = props;
    const sliderButtonRef = useRef<SliderButtonRef | null>(null);

    const onPostSliderSuccessCallback = () => {
        if (sliderButtonRef && sliderButtonRef.current) {
            sliderButtonRef.current.resetSliderButton();
        }
    };

    return (
        <GenericPaymentAlertContext.Provider value={props.paymentAlertConfig ?? DEFAULT_PAYMENT_ALERT_PROPS}>
            <SliderButtonContext.Provider
                value={{
                    sliderButtonRef,
                    onPostSliderSuccessCallback: onPostSliderSuccessCallback,
                }}
            >
                <ShowPayments
                    isTurboPaymentMethod
                    isTurboV2={isTurboV2}
                    turboPayUniqueId={props.uniqueId}
                    forceColorMode={props.forceColorMode}
                >
                    <TurboPayment {...props} />
                </ShowPayments>
            </SliderButtonContext.Provider>
        </GenericPaymentAlertContext.Provider>
    );
};

const TurboPayment = ({
    onPaymentMethodChange,
    onError,
    hideEducationComms = false,
    showLoader,
    onTurboPay,
    showDiscountedValueOnSlider,
    onTurboPayImpressionEvent,
    onTurboPayBtnImpressionEvent,
    onTurboPayClickEvent,
    onPaymentMethodChangeClickEvent,
    isTurboV2 = false,
    cartCallbackOnTurboPaymentChange,
    setForceExtraPaddingOnCart,
    billAmountShownOnCart,
    uniqueId,
    billAmountFromRenderingDetails,
    setIsTurboPayWidgetHidden,
    handleViewDetailedBillClick,
    renderFallback,
    onTurboStripClickEvent,
    onTurboStripImpressionEvent,
    onTooltipImpressionEvent,
    onTooltipClickEvent,
    onTurboLoaded,
    clampDiscountText = false,
    onCvvPopupOpen,
    cartTotal,
}: TurboPaymentProps): JSX.Element | null => {
    const {
        loadingPayments,
        turboPaymentMethod,
        billTotal,
        paymentAmountWithoutDiscount,
        getTurboPaymentSpecificActions,
        juspayMeta,
        showOverlay,
        balanceValue,
        credEligibleData,
        turboPaymentOfferComms,
        paymentMethodDowntime,
        autoPaymethod,
        turboPodHalfCardEvents,
        amountWithDiscount,
        keyFactStatementUrl,
        onKeyFactStatementClick,
    } = useTurboPaymentHandler(onError, onTurboPay, {
        isTurboV2,
        billAmountShownOnCart,
        uniqueId,
        billAmountFromRenderingDetails,
    });

    const { billToCompanyDetails } = useBillToCompanyDetails();

    const isTurboLoaded = useRef(false);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { availableMethods, upiIntentMethodFetchInProgress } = useContext(UpiIntentContext);
    const { checkoutWebXP } = useContext(CheckoutWebDynamicConfigContext);
    const { state: groupCartData } = useContext(GroupOrderCartContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const tooltipDetails = paymentResponse?.tooltip || {};
    const isPopCart = isPopFlow();
    const { turboStrip } = paymentResponse || {};
    const loading = loadingPayments || showLoader;

    const increaseSliderWidth = useMemo(
        () => checkoutWebXP[CHECKOUT_WEB_XP.INCREASE_TURBO_SLIDER_AREA] === BOOLEAN_AS_STRING.TRUE,
        [checkoutWebXP],
    );

    const onClickChangePaymentCta = useCallback(() => {
        onClickChange(clickEvent, groupCartData?.data?.group_cart_id);
        onPaymentMethodChangeClickEvent?.(
            turboPaymentMethod?.paymentCode || "",
            billTotal,
            undefined,
            turboPaymentMethod?.groupName,
            {
                discountText: turboPaymentMethod?.discount,
                bankName: turboPaymentMethod?.meta?.cardIssuer || "",
            },
        );
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
            paymentMethodCode: turboPaymentMethod?.paymentCode || "",
            paymentMethodGroup: turboPaymentMethod?.groupName || "",
            type: "CLICK_CHANGE_PAYMENT_CTA",
        });
        onPaymentMethodChange();
    }, [
        billTotal,
        clickEvent,
        groupCartData?.data?.group_cart_id,
        onPaymentMethodChange,
        onPaymentMethodChangeClickEvent,
        turboPaymentMethod?.paymentCode,
        turboPaymentMethod?.groupName,
    ]);

    const onSlideButtonDrag = useCallback(
        (leftPos, containerWidth) => {
            onTurboPayClickEvent?.(turboPaymentMethod?.paymentCode || "", billTotal, turboPaymentMethod?.groupName, {
                discountText: turboPaymentMethod?.discount,
                bankName: turboPaymentMethod?.meta?.cardIssuer || "",
            });
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
                paymentMethodCode: turboPaymentMethod?.paymentCode || "",
                paymentMethodGroup: turboPaymentMethod?.groupName || "",
                type: "SLIDE_BUTTON_DRAG",
            });
            onClickSlider(clickEvent, Math.floor((leftPos / containerWidth) * 100));
        },
        [billTotal, clickEvent, onTurboPayClickEvent, turboPaymentMethod],
    );
    const onSlideButtonSuccess = useCallback(() => {
        onTurboPayClickEvent?.(turboPaymentMethod?.paymentCode || "", billTotal, turboPaymentMethod?.groupName, {
            discountText: turboPaymentMethod?.discount,
            bankName: turboPaymentMethod?.meta?.cardIssuer || "",
        });
        recordNREvent({
            eventType: NEWRELIC_TABLES.TURBO_PAY_BUTTON_CLICK_DEBUG,
            paymentMethodCode: turboPaymentMethod?.paymentCode || "",
            paymentMethodGroup: turboPaymentMethod?.groupName || "",
            type: "CLICK_ON_PAY",
        });
        getBridgeSdk()
            .vibrate({ duration: 100, amplitude: VibrateAmplitudeTypes.HEAVY })
            .catch(() => void 0);
    }, [billTotal, onTurboPayClickEvent, turboPaymentMethod]);

    const onImpressionChangeTooltipAnimation = useCallback(
        () => onImpressionChangeTooltip(impressionEvent),
        [impressionEvent],
    );

    const onClickChangeTooltipAnimation = useCallback(() => onClickChangeTooltip(clickEvent), [clickEvent]);

    const onPaymentDowntimeWidgetLoad = useCallback(
        () => impressionPayAfterOrder(impressionEvent, true),
        [impressionEvent],
    );

    const campaignKey = paymentResponse?.tooltip?.key || "--";

    const eventOnImpressionTooltip = useCallback(
        (stripMsg?: string) => onTooltipImpressionEvent?.(campaignKey, stripMsg),
        [onTooltipImpressionEvent, campaignKey],
    );

    const eventOnTooltipClose = useCallback(
        (stripMsg?: string) => onTooltipClickEvent?.(campaignKey, stripMsg),
        [onTooltipClickEvent, campaignKey],
    );

    const eventOnTurboStripClick = useCallback(
        (isNewBanner, bannerId, stripMsg) => {
            onTurboStripClickEvent?.(isNewBanner, bannerId, stripMsg);
        },
        [onTurboStripClickEvent],
    );

    const eventOnTurboStripImpression = useCallback(
        (isNewBanner, bannerId, stripMsg) => onTurboStripImpressionEvent?.(isNewBanner, bannerId, stripMsg),
        [onTurboStripImpressionEvent],
    );

    const handleMakePaymentOnFallbackMethod = useCallback(() => {
        onPaymentMethodChangeClickEvent?.("", billTotal, true);
        onPaymentMethodChange();
    }, [billTotal, onPaymentMethodChange, onPaymentMethodChangeClickEvent]);

    const handleTurboStripClick = useCallback(
        (isNewBanner, bannerId, stripMsg) => {
            eventOnTurboStripClick(isNewBanner, bannerId, stripMsg);

            const link = turboStrip?.cta?.link;

            if (link === TurboStripCtaLink.EMI) {
                onPaymentMethodChange(PAYMENT_INITIAL_STATE.EMI_SELECTION);
            } else {
                onPaymentMethodChange();
            }
        },
        [eventOnTurboStripClick, onPaymentMethodChange, turboStrip?.cta?.link],
    );

    useEffect(() => {
        // force extra padding on cart if turbo strip is present
        if (!isEmpty(paymentResponse) && !isEmpty(turboStrip)) {
            setForceExtraPaddingOnCart?.(true);
        }
    }, [turboStrip, setForceExtraPaddingOnCart, paymentResponse]);

    useEffect(() => {
        if (turboPaymentMethod) {
            onTurboPayImpressionEvent?.(
                turboPaymentMethod.paymentCode,
                billTotal,
                undefined,
                turboPaymentMethod.groupName,
                {
                    discountText: turboPaymentMethod?.discount,
                    bankName: turboPaymentMethod?.meta?.cardIssuer || "",
                },
            );
            onTurboPayBtnImpressionEvent?.(turboPaymentMethod.paymentCode, billTotal, turboPaymentMethod.groupName, {
                discountText: turboPaymentMethod?.discount,
                bankName: turboPaymentMethod?.meta?.cardIssuer || "",
            });
        } else if (renderFallback) {
            onTurboPayImpressionEvent?.("", billTotal, true);
        }

        if (!upiIntentMethodFetchInProgress) {
            onUpiAppImpression(impressionEvent, availableMethods, !!turboPaymentMethod);
        }
    }, [
        availableMethods,
        billTotal,
        impressionEvent,
        onTurboPayBtnImpressionEvent,
        onTurboPayImpressionEvent,
        renderFallback,
        turboPaymentMethod,
        upiIntentMethodFetchInProgress,
    ]);

    useEffect(() => {
        if (!loading && !isTurboLoaded.current) {
            isTurboLoaded.current = true;
            onTurboLoaded?.();
        }

        return () => {
            isTurboLoaded.current = false;
        };
    }, [loading, onTurboLoaded]);

    // This is for IM fallback
    if (renderFallback) {
        return (
            <TurboCartFallbackFooter
                cartTotal={cartTotal || "0"}
                viewDetailedBill={handleViewDetailedBillClick}
                makePayment={handleMakePaymentOnFallbackMethod}
                turboStripData={turboStrip}
                onTurboStripClick={handleTurboStripClick}
                onTurboStripImpression={eventOnTurboStripImpression}
                isTurboV2={isTurboV2}
                tooltipDetails={tooltipDetails}
                eventOnImpressionTooltip={eventOnImpressionTooltip}
                eventOnTooltipClose={eventOnTooltipClose}
            />
        );
    }

    return (
        <>
            <CartFooterPaymentWidgets
                setIsTurboPayWidgetHidden={setIsTurboPayWidgetHidden}
                setForceExtraPaddingOnCart={setForceExtraPaddingOnCart}
            />
            <TurboPaymentWidget
                billAmount={billTotal}
                loading={loading}
                paymentMethod={turboPaymentMethod}
                paymentAmountWithoutDiscount={paymentAmountWithoutDiscount}
                onClickChangePaymentCta={onClickChangePaymentCta}
                getTurboPaymentSpecificActions={getTurboPaymentSpecificActions}
                hideEducationComms={hideEducationComms}
                juspayMeta={juspayMeta}
                showOverlay={showOverlay}
                onSlideButtonDrag={onSlideButtonDrag}
                onSlideButtonSuccess={onSlideButtonSuccess}
                onImpressionChangeTooltipAnimation={onImpressionChangeTooltipAnimation}
                onClickChangeTooltipAnimation={onClickChangeTooltipAnimation}
                balanceValue={balanceValue}
                credEligibleData={credEligibleData}
                showDiscountedAmount={showDiscountedValueOnSlider || false}
                turboPaymentOfferComms={turboPaymentOfferComms}
                paymentMethodDowntime={paymentMethodDowntime}
                autoPaymethod={autoPaymethod}
                onPaymentDowntimeWidgetLoad={onPaymentDowntimeWidgetLoad}
                turboPodHalfCardEvents={turboPodHalfCardEvents}
                manageSwipe={manageSwipe}
                increaseSliderWidth={increaseSliderWidth}
                eventOnImpressionTooltip={eventOnImpressionTooltip}
                eventOnTooltipClose={eventOnTooltipClose}
                tooltipDetails={tooltipDetails}
                isTurboV2={isTurboV2}
                isPopCart={isPopCart}
                onClickTurboStrip={handleTurboStripClick}
                onImpressionTurboStrip={eventOnTurboStripImpression}
                turboStrip={turboStrip ?? {}}
                cartCallbackOnTurboPaymentChange={cartCallbackOnTurboPaymentChange}
                billAmountShownOnCart={billAmountShownOnCart}
                uniqueId={uniqueId}
                amountWithDiscount={amountWithDiscount}
                isBillToCompanyExist={billToCompanyDetails.isWidgetShown}
                clampDiscountText={clampDiscountText}
                onCvvPopupOpen={onCvvPopupOpen}
                keyFactStatementUrl={keyFactStatementUrl}
                onKeyFactStatementClick={onKeyFactStatementClick}
            />
        </>
    );
};

export default memo(TurboPaymentWrapper);
